import React, { useState, useRef } from 'react'
import Papa from 'papaparse'

interface ImportTabProps {
  errors: any
  setImportedData: (data: any[]) => void
  setActiveTab: (tab: string) => void
  importedData: any[]
}

interface CsvData {
  [key: string]: string | number
}

interface ParsedCsvResult {
  data: CsvData[]
  headers: string[]
  errors: string[]
}

const ImportTab: React.FC<ImportTabProps> = ({
  errors,
  setImportedData,
  setActiveTab,
  importedData,
}) => {
  const [file, setFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<ParsedCsvResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [headerRow, setHeaderRow] = useState<number>(1)
  const [fieldMappings, setFieldMappings] = useState<{
    [key: string]: {
      index: number
      value: string
      column: string
    }
  }>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  const availableFields = [
    { value: 'studentId', label: 'Student ID' },
    { value: 'firstName', label: 'First Name' },
    { value: 'lastName', label: 'Last Name' },
    { value: 'grade', label: 'Grade' },
    { value: 'race', label: 'Race' },
    { value: 'gender', label: 'Gender' },
    { value: 'caseManager', label: 'Case Manager' },
    { value: 'school', label: 'School' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'description', label: 'Goal Description' },
    { value: 'category', label: 'Goal Category' },
    { value: 'area', label: 'Goal Area' },
    { value: 'objectives', label: 'Goal Objectives' },
  ]

  const handleFileSelect = (selectedFile: File) => {
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile)
      parseCsvFile(selectedFile)
    } else {
      alert('Please select a valid CSV file')
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile) {
      handleFileSelect(selectedFile)
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragOver(false)

    const droppedFile = event.dataTransfer.files[0]
    if (droppedFile) {
      handleFileSelect(droppedFile)
    }
  }

  const parseCsvFile = (file: File) => {
    setIsLoading(true)

    Papa.parse(file, {
      complete: (results) => {
        const headers = results.data[headerRow - 1] as string[]
        const dataRows = results.data.slice(headerRow + 1) as string[][]

        // Convert to objects with headers as keys
        const parsedData: CsvData[] = dataRows
          .filter((row) => row.some((cell) => cell.trim() !== '')) // Filter out empty rows
          .map((row) => {
            const obj: CsvData = {}
            headers.forEach((header, index) => {
              obj[header.trim()] = row[index]?.trim() || ''
            })
            return obj
          })

        const parseErrors = results.errors.map((error) => error.message)

        setCsvData({
          data: parsedData,
          headers: headers.map((h) => h.trim()),
          errors: parseErrors,
        })

        setIsLoading(false)
      },
      header: false,
      skipEmptyLines: true,
      error: (error) => {
        console.error('CSV parsing error:', error)
        setCsvData({
          data: [],
          headers: [],
          errors: [
            error.message || 'An error occurred while parsing the CSV file.',
          ],
        })
        setIsLoading(false)
      },
    })
  }

  const handleImport = () => {
    if (csvData && csvData.data.length > 0) {
      const transformedData: any[] = []

      // Get the header row for reference
      const headers = csvData.headers

      // Detect import type based on field mappings
      const goalFields = ['description', 'category', 'area', 'objectives']
      const hasGoalFields = Object.keys(fieldMappings).some((key) =>
        goalFields.includes(key),
      )
      const isGoalsImport = hasGoalFields

      csvData.data.forEach((row: any, index: number) => {
        try {
          // Create raw row data for error reporting
          const rawRowData: any = {}
          headers.forEach((header) => {
            rawRowData[header] = row[header] || ''
          })

          // Build data object from field mappings
          const mappedData: any = {}

          Object.entries(fieldMappings).forEach(([field, mapping]) => {
            const columnName = mapping.column
            if (row[columnName] !== undefined) {
              mappedData[field] = row[columnName]
            }
          })

          // Validate required fields based on import type
          if (isGoalsImport) {
            // Goals import: require studentId, firstName, lastName, category, area, description, objectives
            const hasRequiredFields =
              mappedData.studentId?.trim() &&
              mappedData.firstName?.trim() &&
              mappedData.lastName?.trim() &&
              mappedData.category?.trim() &&
              mappedData.area?.trim() &&
              mappedData.description?.trim() &&
              mappedData.objectives?.trim()

            if (!hasRequiredFields) {
              // Skip rows without required goal fields
              return
            }
          } else {
            // Student/Accommodation import: require studentId or name
            const hasStudentId =
              fieldMappings.studentId && mappedData.studentId?.trim()
            const hasName =
              mappedData.firstName?.trim() && mappedData.lastName?.trim()

            if (!hasStudentId && !hasName) {
              // Skip rows without sufficient identification
              return
            }
          }

          transformedData.push({
            ...mappedData,
            importedAt: new Date().toISOString(),
            _importType: isGoalsImport ? 'goals' : 'students',
            // Add the original CSV row number (add 1 because arrays are 0-indexed, add headerRow to account for header)
            _originalRowNumber: index + headerRow + 1,
          })
        } catch (error) {
          // Create raw row data for error reporting
          const rawRowData: any = {}
          headers.forEach((header) => {
            rawRowData[header] = row[header] || ''
          })

          transformedData.push({
            _processingError:
              error instanceof Error
                ? error.message
                : 'Unknown processing error',
            _originalRowNumber: index + headerRow + 1,
          })
        }
      })

      // Append new data to existing importedData instead of overwriting
      const updatedImportedData = [...importedData, ...transformedData]
      setImportedData(updatedImportedData)

      // Navigate to the Final tab
      setActiveTab('final')

      // Clear current CSV data after successful import
      clearData()
    }
  }

  const clearData = () => {
    setFile(null)
    setCsvData(null)
    setFieldMappings({})
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const fieldColumnChange = (value: string, header: string, index: number) => {
    setFieldMappings((prev) => {
      const newMappings = { ...prev }

      // Define goal and accommodation fields
      const goalFields = ['description', 'category', 'area', 'objectives']
      const isGoalField = goalFields.includes(value)
      const isAccommodationField = value === 'accommodation'

      // Check for existing conflicting mappings
      const hasGoalFields = Object.keys(newMappings).some((key) =>
        goalFields.includes(newMappings[key].value),
      )
      const hasAccommodation = Object.keys(newMappings).some(
        (key) => newMappings[key].value === 'accommodation',
      )

      // Prevent mixing goal and accommodation fields
      if (isGoalField && hasAccommodation) {
        alert(
          'Cannot select Goal fields when Accommodation is already selected. Please clear the Accommodation mapping first.',
        )
        return prev
      }
      if (isAccommodationField && hasGoalFields) {
        alert(
          'Cannot select Accommodation when Goal fields are already selected. Please clear Goal field mappings first.',
        )
        return prev
      }

      // First, remove any existing mapping for this field value to avoid duplicates
      Object.keys(newMappings).forEach((key) => {
        if (newMappings[key].index === index) {
          delete newMappings[key]
        }
      })

      if (value === '') {
        // If empty value, we've already removed it above
        return newMappings
      } else {
        // Add new mapping
        newMappings[value] = {
          index,
          value: value,
          column: header,
        }
        return newMappings
      }
    })
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">
        Import Student Data
      </h3>
      <p>
        Drop in your CSV file below to import student data, including
        accommodations and goals. Relevant columns in your CSV should be mapped
        correctly to TrackIEP fields such student First Name, Last Name, Student
        ID, Accommodations, Goal Descriptions, etc. Note that StudentID is a
        required field in order to either create a new student or to update an
        existing student with new data.
      </p>

      {!csvData && (
        <>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              What row contains your headers?
            </label>
            <input
              type="number"
              min="1"
              value={headerRow}
              onChange={(e) => setHeaderRow(parseInt(e.target.value) || 1)}
              className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="0"
            />
            <p className="text-xs text-gray-500">
              Row number (0-based indexing)
            </p>
          </div>

          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="space-y-4">
                <div className="text-gray-600">
                  <p className="text-lg">Drop your CSV file here, or</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Browse Files
                  </button>
                </div>
                <p className="text-sm text-gray-500">
                  Only CSV files are accepted
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>

            {file && (
              <div className="bg-gray-50 p-4 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Selected file:
                    </p>
                    <p className="text-sm text-gray-600">
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </p>
                  </div>
                  <button
                    onClick={clearData}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Parsing CSV file...</p>
        </div>
      )}

      {/* CSV Data Preview */}
      {csvData && !isLoading && (
        <div className="space-y-4">
          {csvData.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-red-800 mb-2">
                Parsing Errors:
              </h4>
              <ul className="text-sm text-red-700 space-y-1">
                {csvData.errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {csvData.data.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-md font-medium text-gray-900">
                  Preview ({csvData.data.length} records found)
                </h4>
                <div className="space-x-2">
                  <button
                    onClick={clearData}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Clear
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={Object.keys(fieldMappings).length === 0}
                    className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      Object.keys(fieldMappings).length > 0
                        ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    Import Student Data
                  </button>
                </div>
              </div>

              {/* Data Table */}
              <div className="overflow-x-scroll border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {csvData.headers.map((header, index) => (
                        <th
                          key={index}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {csvData.headers.map((header, index) => (
                        <th
                          key={index}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          <select
                            style={{ minWidth: '150px' }}
                            value={
                              Object.keys(fieldMappings).find(
                                (key) => fieldMappings[key].index === index,
                              ) || ''
                            }
                            onChange={(e) =>
                              fieldColumnChange(e.target.value, header, index)
                            }
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white"
                          >
                            <option value="">Select field...</option>
                            {availableFields
                              .filter((field) => {
                                // Show field if it's not already mapped, or if it's currently mapped to this column
                                const currentlyMappedField = Object.keys(
                                  fieldMappings,
                                ).find(
                                  (key) => fieldMappings[key].index === index,
                                )
                                return (
                                  !Object.keys(fieldMappings).includes(
                                    field.value,
                                  ) || field.value === currentlyMappedField
                                )
                              })
                              .map((field, fieldIndex) => (
                                <option key={fieldIndex} value={field.value}>
                                  {field.label}
                                </option>
                              ))}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {csvData.data.slice(0, 10).map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={
                          rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }
                      >
                        {csvData.headers.map((header, colIndex) => {
                          const cellValue = String(row[header])
                          return (
                            <td
                              key={colIndex}
                              className="px-6 py-4 text-sm text-gray-900"
                            >
                              <div
                                className="truncate max-w-sm"
                                title={cellValue}
                              >
                                {cellValue}
                              </div>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {csvData.data.length > 10 && (
                  <div className="bg-gray-50 px-6 py-3 text-sm text-gray-500 text-center">
                    Showing first 10 of {csvData.data.length} records
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Original errors display */}
      {errors && errors.length > 0 && (
        <div className="mt-8">
          <h4 className="text-md font-medium text-gray-900 mb-2">
            System Errors:
          </h4>
          <div className="flex flex-col space-y-2">
            {errors.map((error: any, index: number) => (
              <p key={index} className="text-gray-600">
                {error.firstName}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ImportTab
